import React from "react";

/**
 * <PictureAsset />
 *
 * Single point of control for responsive image delivery across the site.
 * Renders a <picture> element that serves:
 *   • WebP (preferred, smaller) at 1600w + 3200w
 *   • JPEG or PNG (fallback for older Safari) at 1600w + 3200w
 *
 * The browser picks the best supported format and the width closest to the
 * displayed size on the current device, so a phone never downloads the
 * 3200-wide file intended for a 5K desktop.
 *
 * Props:
 *   base           REQUIRED. Path without extension or width, relative to /.
 *                  Example: "/assets/homepage/scenes/01-immersive"
 *                  The component appends "-1600.webp", "-3200.webp",
 *                  "-1600.jpg", "-3200.jpg" itself.
 *   alt            REQUIRED. Alt text for the <img> fallback.
 *   fallbackExt    "jpg" (default) or "png" — the fallback format.
 *   sizes          Sizes attribute for responsive selection.
 *                  Default "100vw" (full-viewport-width). Override per call
 *                  site when the image occupies a fixed fraction of viewport.
 *   className      Applied to the <img>; layout/animation classes go here.
 *   loading        Native lazy-loading; defaults to "lazy". Pass "eager" for
 *                  above-the-fold hero images.
 *   fetchPriority  React prop; passes through as fetchpriority attr.
 *   imgProps       Any extra props for the <img> (style, id, data-*, etc.).
 */
export function PictureAsset({
  base,
  alt,
  fallbackExt = "jpg",
  sizes = "100vw",
  className,
  loading = "lazy",
  fetchPriority,
  imgRef,
  ...imgProps
}) {
  const webp1600 = `${base}-1600.webp`;
  const webp3200 = `${base}-3200.webp`;
  const fb1600 = `${base}-1600.${fallbackExt}`;
  const fb3200 = `${base}-3200.${fallbackExt}`;
  const fbMime = fallbackExt === "png" ? "image/png" : "image/jpeg";

  return (
    <picture>
      <source
        type="image/webp"
        srcSet={`${webp1600} 1600w, ${webp3200} 3200w`}
        sizes={sizes}
      />
      <source
        type={fbMime}
        srcSet={`${fb1600} 1600w, ${fb3200} 3200w`}
        sizes={sizes}
      />
      <img
        ref={imgRef}
        src={fb3200}
        alt={alt}
        className={className}
        loading={loading}
        fetchpriority={fetchPriority}
        {...imgProps}
      />
    </picture>
  );
}

/**
 * <TransparentPictureAsset />
 *
 * Specialised variant for images with transparency (currently only the
 * homepage process illustration). No responsive tiers because these
 * illustrations are used at a fixed layout size; instead ships one lossless
 * WebP with alpha + the original PNG as fallback.
 */
export function TransparentPictureAsset({ base, alt, className, loading = "lazy", ...imgProps }) {
  return (
    <picture>
      <source type="image/webp" srcSet={`${base}.webp`} />
      <img
        src={`${base}.png`}
        alt={alt}
        className={className}
        loading={loading}
        {...imgProps}
      />
    </picture>
  );
}
